import { AnimatePresence, motion } from 'framer-motion';
import {
  ChevronLeft,
  ChevronRight,
  Globe,
  Map,
  Sparkles,
  Trash2,
  Trophy,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { CountryCard } from '@/components/CountryCard';
import { ScopeSelector } from '@/components/ScopeSelector';
import {
  ModeToggle,
  type RatingMode,
  TournamentMode,
  TournamentProvider,
} from '@/components/tournament';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { WorldMap } from '@/components/WorldMap';
import {
  getCountriesForScope,
  getDefaultScope,
  getRatingColor,
  getScopeProgress,
  type Rating,
  ratingLabels,
  ratingScopes,
} from '@/data/countries';
import { loadWorldData } from '@/lib/map-data';
import { syncService } from '@/lib/sync-service';
import {
  getTournamentProgress,
  loadAppMode,
  saveAppMode,
} from '@/lib/tournament';
import { cn } from '@/lib/utils';
import { submitVote } from '@/lib/vote-service';
import { AdminPage } from '@/pages/AdminPage';
import { BracketViewDebug } from '@/pages/BracketViewDebug';
import { CountryLocatorDebug } from '@/pages/CountryLocatorDebug';
import { LeaderboardPage } from '@/pages/LeaderboardPage';
import { LiveMapPage } from '@/pages/LiveMapPage';

type AppPage =
  | 'home'
  | 'admin'
  | 'leaderboard'
  | 'livemap'
  | 'bracketviewdebug'
  | 'countrylocatordebug';

interface HomePageProps {
  currentScopeId: string;
  setCurrentScopeId: (id: string) => void;
  ratings: Record<string, number>;
  setRatings: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  mode: RatingMode;
  setMode: (mode: RatingMode) => void;
}

function HomePage({
  currentScopeId,
  setCurrentScopeId,
  ratings,
  setRatings,
  mode,
  setMode,
}: HomePageProps) {
  // Persist index per scope
  const [scopeIndices, setScopeIndices] = useState<Record<string, number>>(
    () => {
      const saved = localStorage.getItem('country-scope-indices');
      return saved ? JSON.parse(saved) : {};
    },
  );

  // Persist map dialog open state
  const [mapDialogOpen, setMapDialogOpen] = useState(() => {
    return localStorage.getItem('map-dialog-open') === 'true';
  });

  // Derived current index from scope indices
  const currentIndex = scopeIndices[currentScopeId] ?? 0;
  const setCurrentIndex = useCallback(
    (updater: number | ((prev: number) => number)) => {
      setScopeIndices((prev) => {
        const currentVal = prev[currentScopeId] ?? 0;
        const newVal =
          typeof updater === 'function' ? updater(currentVal) : updater;
        return { ...prev, [currentScopeId]: newVal };
      });
    },
    [currentScopeId],
  );

  // Save scope indices to localStorage
  useEffect(() => {
    localStorage.setItem('country-scope-indices', JSON.stringify(scopeIndices));
  }, [scopeIndices]);

  // Save map dialog state to localStorage
  useEffect(() => {
    localStorage.setItem('map-dialog-open', mapDialogOpen.toString());
  }, [mapDialogOpen]);

  // Get countries for current scope
  const scopeCountries = getCountriesForScope(currentScopeId);
  const currentCountry = scopeCountries[currentIndex];
  const scopeProgress = getScopeProgress(currentScopeId, ratings);
  const currentScope = ratingScopes.find((s) => s.id === currentScopeId);

  // Navigation handlers
  const handlePrevious = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    }
  }, [currentIndex]);

  const handleNext = useCallback(() => {
    if (currentIndex < scopeCountries.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    }
  }, [currentIndex, scopeCountries.length]);

  // Rating handler
  const handleRate = useCallback(
    (countryCode: string, rating: number) => {
      setRatings((prev) => ({
        ...prev,
        [countryCode]: rating,
      }));

      // Auto-advance
      if (currentIndex < scopeCountries.length - 1) {
        setTimeout(() => {
          setCurrentIndex((prev) => prev + 1);
        }, 150);
      }
    },
    [currentIndex, scopeCountries.length],
  );

  // Clear ratings for current scope
  const handleClearScope = useCallback(() => {
    const scope = ratingScopes.find((s) => s.id === currentScopeId);
    if (!scope) return;

    setRatings((prev) => {
      const next = { ...prev };
      scope.countryCodes.forEach((code) => {
        delete next[code];
      });
      return next;
    });
    setCurrentIndex(0);
    toast.success('Ratings cleared');
  }, [currentScopeId]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return;
      }

      const key = e.key;

      // Handle 1-5 for ratings
      if (['1', '2', '3', '4', '5'].includes(key) && currentCountry) {
        const rating = parseInt(key);
        handleRate(currentCountry.code, rating);
        e.preventDefault();
      }

      // Handle arrow keys
      if (key === 'ArrowLeft') {
        handlePrevious();
        e.preventDefault();
      } else if (key === 'ArrowRight') {
        handleNext();
        e.preventDefault();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentCountry, handleRate, handlePrevious, handleNext]);

  // Guard for empty scope
  if (!currentCountry) {
    return (
      <div className="h-dvh flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">
            No countries in this scope.
          </p>
          <ScopeSelector
            currentScopeId={currentScopeId}
            onScopeChange={setCurrentScopeId}
            ratings={ratings}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="h-dvh w-full overflow-hidden bg-background">
      {/* Desktop: Two-column layout | Mobile: Stacked */}
      <div className="h-full flex flex-col lg:flex-row">
        {/* Sidebar - Desktop only */}
        <aside className="hidden lg:flex flex-col w-80 xl:w-96 border-r border-border/60 bg-card/50">
          {/* Header */}
          <header className="p-6 xl:p-8 border-b border-border/40">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h1 className="font-display text-2xl xl:text-3xl tracking-tight text-foreground">
                  Country Tier
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Rate and rank countries
                </p>
              </div>
            </div>
            {/* Mode Toggle */}
            <div className="mt-4">
              <ModeToggle mode={mode} onModeChange={setMode} />
            </div>
          </header>

          {/* Region selector */}
          <div className="flex-1 overflow-y-auto p-4 xl:p-6">
            <div className="space-y-2">
              {ratingScopes.map((scope) => {
                const progress = getScopeProgress(scope.id, ratings);
                const isActive = scope.id === currentScopeId;
                return (
                  <button
                    key={scope.id}
                    onClick={() => setCurrentScopeId(scope.id)}
                    className={cn(
                      'w-full text-left px-4 py-3 rounded-xl transition-all',
                      'border-2 group',
                      isActive
                        ? 'border-primary bg-primary/5'
                        : 'border-transparent hover:bg-muted/50',
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{scope.icon}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span
                            className={cn(
                              'font-semibold text-sm',
                              isActive ? 'text-primary' : 'text-foreground',
                            )}
                          >
                            {scope.name}
                          </span>
                          <span className="text-xs text-muted-foreground tabular-nums">
                            {progress.rated}/{progress.total}
                          </span>
                        </div>
                        <div className="h-1 bg-muted rounded-full mt-2 overflow-hidden">
                          <div
                            className={cn(
                              'h-full rounded-full transition-all duration-500',
                              progress.percentage === 100
                                ? 'bg-green-500'
                                : 'bg-primary',
                            )}
                            style={{ width: `${progress.percentage}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Sidebar footer - Legend */}
          <div className="p-4 xl:p-6 border-t border-border/40">
            {/* Actions */}
            <div className="flex gap-2 mt-4 pt-4 border-t border-border/40">
              <Button
                variant="outline"
                size="sm"
                className="flex-1 text-xs"
                onClick={() => {
                  window.location.hash = 'leaderboard';
                }}
              >
                <Trophy className="w-3.5 h-3.5 mr-1.5 text-yellow-500" />
                Leaderboard
              </Button>
              <Dialog open={mapDialogOpen} onOpenChange={setMapDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 text-xs"
                  >
                    <Map className="w-3.5 h-3.5 mr-1.5" />
                    Map
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-xl font-display flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-primary" />
                      Your {currentScope?.name} Results
                    </DialogTitle>
                    <DialogDescription>
                      Your country ratings visualized on a world map
                    </DialogDescription>
                  </DialogHeader>
                  <div className="mt-4">
                    <WorldMap
                      ratings={ratings}
                      scopeCountryCodes={currentScope?.countryCodes}
                      scopeId={currentScopeId}
                      mode={mode}
                      scopeName={currentScope?.name}
                    />
                  </div>
                </DialogContent>
              </Dialog>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={scopeProgress.rated === 0}
                    className="text-xs hover:bg-destructive/10 hover:text-destructive hover:border-destructive/50"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Clear all ratings?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will clear all {scopeProgress.rated} ratings for{' '}
                      {currentScope?.name}. This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleClearScope}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Clear Ratings
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </aside>

        {/* Main content area */}
        <main className="flex-1 min-h-0 flex flex-col min-w-0 relative overflow-hidden">
          {/* Mobile header - single row */}
          <div className="lg:hidden shrink-0 px-4 pt-4 pb-2">
            <div className="flex items-center gap-2">
              <ModeToggle mode={mode} onModeChange={setMode} />
              <div className="flex-1 min-w-0">
                <ScopeSelector
                  currentScopeId={currentScopeId}
                  onScopeChange={setCurrentScopeId}
                  ratings={ratings}
                  compact
                />
              </div>
              <div className="flex gap-1.5 shrink-0">
                <Dialog open={mapDialogOpen} onOpenChange={setMapDialogOpen}>
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-10 w-10 border-2"
                    >
                      <Map className="w-4 h-4" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-[95vw] w-full max-h-[95vh] overflow-y-auto p-4 sm:p-6">
                    <DialogHeader>
                      <DialogTitle className="text-xl font-display flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-primary" />
                        Your {currentScope?.name} Results
                      </DialogTitle>
                      <DialogDescription>
                        Your country ratings visualized on a world map
                      </DialogDescription>
                    </DialogHeader>
                    <div className="mt-4">
                      <WorldMap
                        ratings={ratings}
                        scopeCountryCodes={currentScope?.countryCodes}
                        scopeId={currentScopeId}
                        mode={mode}
                        scopeName={currentScope?.name}
                      />
                    </div>
                  </DialogContent>
                </Dialog>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      disabled={scopeProgress.rated === 0}
                      className="h-10 w-10 border-2 hover:bg-destructive/10 hover:text-destructive hover:border-destructive/50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Clear all ratings?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will clear all {scopeProgress.rated} ratings for{' '}
                        {currentScope?.name}. This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleClearScope}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Clear Ratings
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </div>

          {/* Progress bar */}
          <div className="shrink-0 px-4 lg:px-8 xl:px-12 py-2 lg:py-4">
            <div className="flex items-center gap-4">
              <div className="flex-1 h-1 lg:h-1.5 bg-muted rounded-full overflow-hidden">
                <motion.div
                  initial={false}
                  animate={{ width: `${scopeProgress.percentage}%` }}
                  transition={{ duration: 0.3 }}
                  className={cn(
                    'h-full rounded-full',
                    scopeProgress.percentage === 100
                      ? 'bg-green-500'
                      : 'bg-primary',
                  )}
                />
              </div>
              <span className="text-xs lg:text-sm font-medium text-muted-foreground tabular-nums whitespace-nowrap">
                {currentIndex + 1} of {scopeCountries.length}
              </span>
            </div>
          </div>

          {/* Country card - centered on desktop */}
          <div className="flex-1 min-h-0 px-4 lg:px-8 xl:px-12 pb-2 lg:pb-6">
            <div className="h-full max-w-3xl mx-auto">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentCountry.code}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="h-full"
                >
                  <CountryCard
                    country={currentCountry}
                    rating={ratings[currentCountry.code] ?? null}
                    onRate={(rating) => handleRate(currentCountry.code, rating)}
                  />
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {/* Navigation footer */}
          <div className="shrink-0 px-4 lg:px-8 xl:px-12 pb-4 lg:pb-6">
            <div className="max-w-3xl mx-auto flex items-center justify-between gap-4">
              <Button
                variant="outline"
                onClick={handlePrevious}
                disabled={currentIndex === 0}
                className="font-semibold border-2 h-11 lg:h-12 px-5 lg:px-6"
              >
                <ChevronLeft className="w-4 h-4 mr-1.5" />
                Previous
              </Button>

              {/* Live Map button - mobile */}
              <Button
                variant="outline"
                onClick={() => {
                  window.location.hash = 'livemap';
                }}
                className="lg:hidden font-semibold border-2 h-11 px-4"
              >
                <Globe className="w-4 h-4 mr-1.5 text-blue-500" />
                Live
              </Button>

              {/* Keyboard hints + Live Map button - desktop */}
              <div className="hidden lg:flex items-center gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    window.location.hash = 'livemap';
                  }}
                  className="font-semibold border-2 h-12 px-5"
                >
                  <Globe className="w-4 h-4 mr-1.5 text-blue-500" />
                  Live Map
                </Button>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <kbd className="px-2 py-1 rounded-md bg-muted/80 border border-border font-mono text-[11px]">
                    1-5
                  </kbd>
                  <span>rate</span>
                  <span className="mx-2 text-border">|</span>
                  <kbd className="px-2 py-1 rounded-md bg-muted/80 border border-border font-mono text-[11px]">
                    ←
                  </kbd>
                  <kbd className="px-2 py-1 rounded-md bg-muted/80 border border-border font-mono text-[11px]">
                    →
                  </kbd>
                  <span>navigate</span>
                </div>
              </div>

              <Button
                variant="outline"
                onClick={handleNext}
                disabled={currentIndex === scopeCountries.length - 1}
                className="font-semibold border-2 h-11 lg:h-12 px-5 lg:px-6"
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1.5" />
              </Button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default function App() {
  // Page routing via hash
  const [page, setPage] = useState<AppPage>(() => {
    const hash = window.location.hash;
    if (hash === '#alexadminpage') return 'admin';
    if (hash === '#leaderboard') return 'leaderboard';
    if (hash === '#livemap') return 'livemap';
    if (hash === '#bracketviewdebug') return 'bracketviewdebug';
    if (hash === '#countrylocatordebug') return 'countrylocatordebug';
    return 'home';
  });

  // Persist scope selection
  const [currentScopeId, setCurrentScopeId] = useState<string>(() => {
    const saved = localStorage.getItem('country-scope');
    return saved ?? getDefaultScope().id;
  });

  // Persist ratings
  const [ratings, setRatings] = useState<Record<string, number>>(() => {
    const saved = localStorage.getItem('country-ratings');
    return saved ? JSON.parse(saved) : {};
  });

  // Persist mode
  const [mode, setMode] = useState<RatingMode>(() => loadAppMode());

  // Handle hash changes for page navigation
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash === '#alexadminpage') setPage('admin');
      else if (hash === '#leaderboard') setPage('leaderboard');
      else if (hash === '#livemap') setPage('livemap');
      else if (hash === '#bracketviewdebug') setPage('bracketviewdebug');
      else if (hash === '#countrylocatordebug') setPage('countrylocatordebug');
      else setPage('home');
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Save scope to localStorage
  useEffect(() => {
    localStorage.setItem('country-scope', currentScopeId);
  }, [currentScopeId]);

  // Save ratings to localStorage
  useEffect(() => {
    localStorage.setItem('country-ratings', JSON.stringify(ratings));
  }, [ratings]);

  // Save mode to localStorage
  useEffect(() => {
    saveAppMode(mode);
  }, [mode]);

  // Preload map data
  useEffect(() => {
    loadWorldData();
  }, []);

  // Initialize sync service (once on mount)
  useEffect(() => {
    syncService.init();
  }, []);

  // Track previous ratings to detect changes for sync
  const prevRatingsRef = useRef<Record<string, number>>({});

  // Submit rating changes immediately to server
  useEffect(() => {
    const prevRatings = prevRatingsRef.current;

    // Find changed ratings and submit immediately
    for (const [code, rating] of Object.entries(ratings)) {
      if (prevRatings[code] !== rating) {
        // Submit directly to API for instant leaderboard updates
        submitVote(code, rating).catch((err) => {
          console.error('Failed to submit vote:', err);
        });
        // Also queue for offline backup
        syncService.queueRating(code, rating);
      }
    }

    // Update ref for next comparison
    prevRatingsRef.current = { ...ratings };
  }, [ratings]);

  // Cross-tab sync via storage events
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'country-ratings' && e.newValue) {
        try {
          const newRatings = JSON.parse(e.newValue);
          // Update state without triggering sync (already synced by other tab)
          prevRatingsRef.current = newRatings;
          setRatings(newRatings);
        } catch {
          // Ignore parse errors
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Handle tournament ratings completion
  const handleTournamentRatingsComplete = useCallback(
    (newRatings: Record<string, number>) => {
      setRatings((prev) => ({
        ...prev,
        ...newRatings,
      }));
      toast.success('Tournament ratings applied!');
    },
    [],
  );

  // Admin page
  if (page === 'admin') {
    return (
      <AdminPage
        onBack={() => {
          window.location.hash = '';
          setPage('home');
        }}
      />
    );
  }

  // Leaderboard page
  if (page === 'leaderboard') {
    return (
      <LeaderboardPage
        onBack={() => {
          window.location.hash = '';
          setPage('home');
        }}
      />
    );
  }

  // Live map page
  if (page === 'livemap') {
    return (
      <LiveMapPage
        onBack={() => {
          window.location.hash = '';
          setPage('home');
        }}
      />
    );
  }

  // Bracket view debug page
  if (page === 'bracketviewdebug') {
    return (
      <BracketViewDebug
        onBack={() => {
          window.location.hash = '';
          setPage('home');
        }}
      />
    );
  }

  // CountryLocator debug page
  if (page === 'countrylocatordebug') {
    return (
      <CountryLocatorDebug
        onBack={() => {
          window.location.hash = '';
          setPage('home');
        }}
      />
    );
  }

  if (mode === 'tournament') {
    return (
      <div className="h-dvh w-full overflow-hidden bg-background">
        <div className="h-full flex flex-col lg:flex-row">
          {/* Sidebar - Desktop only */}
          <aside className="hidden lg:flex flex-col w-80 xl:w-96 border-r border-border/60 bg-card/50">
            {/* Header */}
            <header className="p-6 xl:p-8 border-b border-border/40">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h1 className="font-display text-2xl xl:text-3xl tracking-tight text-foreground">
                    Where in the World
                  </h1>
                  <p className="text-sm text-muted-foreground mt-1">
                    Rate your travel bucket list
                  </p>
                </div>
              </div>
              {/* Mode Toggle */}
              <div className="mt-4">
                <ModeToggle mode={mode} onModeChange={setMode} />
              </div>
            </header>

            {/* Scope selector for tournament */}
            <div className="flex-1 overflow-y-auto p-4 xl:p-6">
              <div className="space-y-2">
                {ratingScopes.map((scope) => {
                  // Use tournament progress in tournament mode
                  const progress = getTournamentProgress(scope.id);
                  const isActive = scope.id === currentScopeId;
                  return (
                    <button
                      key={scope.id}
                      onClick={() => setCurrentScopeId(scope.id)}
                      className={cn(
                        'w-full text-left px-4 py-3 rounded-xl transition-all',
                        'border-2 group',
                        isActive
                          ? 'border-primary bg-primary/5'
                          : 'border-transparent hover:bg-muted/50',
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{scope.icon}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <span
                              className={cn(
                                'font-semibold text-sm',
                                isActive ? 'text-primary' : 'text-foreground',
                              )}
                            >
                              {scope.name}
                            </span>
                            <span className="text-xs text-muted-foreground tabular-nums">
                              {progress.rated}/{progress.total}
                            </span>
                          </div>
                          <div className="h-1 bg-muted rounded-full mt-2 overflow-hidden">
                            <div
                              className={cn(
                                'h-full rounded-full transition-all duration-500',
                                progress.percentage === 100
                                  ? 'bg-green-500'
                                  : 'bg-primary',
                              )}
                              style={{ width: `${progress.percentage}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Rating legend */}
            <div className="p-4 xl:p-6 border-t border-border/40">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                Rating Guide
              </p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                {([1, 2, 3, 4, 5] as Rating[]).map((rating) => (
                  <div key={rating} className="flex items-center gap-2">
                    <div
                      className="w-2.5 h-2.5 rounded-full shrink-0 ring-1 ring-black/5"
                      style={{ backgroundColor: getRatingColor(rating) }}
                    />
                    <span className="text-xs text-muted-foreground">
                      <span className="font-medium text-foreground">
                        {rating}
                      </span>{' '}
                      {ratingLabels[rating].label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </aside>

          {/* Main content area */}
          <main className="flex-1 flex flex-col min-w-0 relative">
            {/* Mobile header - single row */}
            <div className="lg:hidden shrink-0 px-4 pt-4 pb-2">
              <div className="flex items-center gap-2">
                <ModeToggle mode={mode} onModeChange={setMode} />
                <div className="flex-1 min-w-0">
                  <ScopeSelector
                    currentScopeId={currentScopeId}
                    onScopeChange={setCurrentScopeId}
                    ratings={ratings}
                    progressOverride={getTournamentProgress(currentScopeId)}
                    compact
                  />
                </div>
              </div>
            </div>

            {/* Tournament mode content */}
            <div className="flex-1 min-h-0 h-0 overflow-hidden">
              <TournamentProvider
                scopeId={currentScopeId}
                onRatingsComplete={handleTournamentRatingsComplete}
              >
                <TournamentMode />
              </TournamentProvider>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <HomePage
      currentScopeId={currentScopeId}
      setCurrentScopeId={setCurrentScopeId}
      ratings={ratings}
      setRatings={setRatings}
      mode={mode}
      setMode={setMode}
    />
  );
}
